import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Papa from 'papaparse'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

type Props = {
  isOpen: boolean
  onClose: () => void
}

type ParsedRow = Record<string, string>

export function ContactsImportModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation('contacts')
  const [headers, setHeaders] = useState<string[]>([])
  const [previewData, setPreviewData] = useState<ParsedRow[]>([])
  const [allData, setAllData] = useState<ParsedRow[]>([])
  const [isImporting, setIsImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [errors, setErrors] = useState<string[]>([])

  const queryClient = useQueryClient()

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    if (!selected) return

    setErrors([])
    setProgress(0)

    Papa.parse<ParsedRow>(selected, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.meta.fields) {
          setHeaders(results.meta.fields)
        }
        setAllData(results.data)
        setPreviewData(results.data.slice(0, 5))
      },
      error: (error) => {
        setErrors([`Fehler beim Parsen der CSV: ${error.message}`])
      }
    })
  }

  async function handleImport() {
    if (!allData.length) return

    setIsImporting(true)
    setErrors([])
    let successCount = 0
    let errCount = 0

    const client = apiClient

    for (let i = 0; i < allData.length; i++) {
      const row = allData[i]
      try {
        const body = {
          contact: {
            name: row.name || t('importModal.unknown'),
            email: row.email || null,
            phone: row.phone || null,
            address: row.address || null,
            helium_wallet: row.helium_wallet || null,
            btc_wallet: row.btc_wallet || null,
            eth_wallet: row.eth_wallet || null,
            usdt_wallet: row.usdt_wallet || null,
            usdc_wallet: row.usdc_wallet || null,
            iban: row.iban || null,
            paypal: row.paypal || null,
            payment_method: row.payment_method || null,
            currency: row.currency || null,
            monthly_report: row.monthly_report === 'true',
            tag_list: row.tag_list || '',
            notes: row.notes || null,
          }
        }

        const res = await client.POST('/api/v2/contacts', { body })
        if (res.error) throw new Error(JSON.stringify(res.error))

        successCount++
      } catch (err) {
        errCount++
        const msg = err instanceof Error ? err.message : String(err)
        setErrors((prev) => [...prev, `${t('importModal.rowLabel')} ${i + 1}: ${msg}`])
      } finally {
        setProgress(Math.round(((i + 1) / allData.length) * 100))
      }
    }

    setIsImporting(false)
    void queryClient.invalidateQueries({ queryKey: ['contacts'] })

    if (errCount === 0) {
      // F15 (FIN-SWEEP-FE-7): Singular/Plural via i18next-Pluralisierung (count)
      alert(t('importModal.successAlert', { count: successCount }))
      handleClose()
    } else {
      alert(t('importModal.failedAlert', { success: successCount, failed: errCount }))
    }
  }

  function handleClose() {
    setHeaders([])
    setPreviewData([])
    setAllData([])
    setProgress(0)
    setErrors([])
    setIsImporting(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-contacts-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isImporting) handleClose()
      }}
      data-testid="import-contacts-modal"
    >
      <div className="modal" style={{ maxWidth: '800px', width: '90%' }}>
        <div className="modal-header">
          <h2 id="import-contacts-title">{t('importModal.title')}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('importModal.closeAriaLabel')}
            onClick={handleClose}
            disabled={isImporting}
          >
            &times;
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="csv-upload">{t('importModal.csvLabel')}</label>
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isImporting}
              className="input"
              data-testid="csv-upload-input"
            />
          </div>

          {previewData.length > 0 && (
            <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
              <h3>{t('importModal.previewTitle')}</h3>
              <table className="table" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    {headers.map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, idx) => (
                    <tr key={idx}>
                      {headers.map((h) => (
                        <td key={h}>{row[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {isImporting && (
            <div style={{ marginTop: '1rem' }}>
              <p>{t('importModal.progressText', { progress })}</p>
              <div style={{ width: '100%', height: '10px', background: '#eee', borderRadius: '5px' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: 'var(--color-primary, blue)', borderRadius: '5px', transition: 'width 0.2s' }} />
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <div className="error-state" style={{ marginTop: '1rem', textAlign: 'left', maxHeight: '150px', overflowY: 'auto' }}>
              <p><strong>{t('importModal.errorsTitle')}</strong></p>
              <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                {errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleClose}
            disabled={isImporting}
          >
            {t('importModal.cancelBtn')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleImport()}
            disabled={!allData.length || isImporting}
            data-testid="start-import-btn"
          >
            {isImporting ? t('importModal.submitPending') : t('importModal.submitBtn')}
          </button>
        </div>
      </div>
    </div>
  )
}
